import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import Linkify from 'react-linkify';
import styled, { css } from 'styled-components';
import useTracking from '../../hooks/useTracking';
import {
  AQUAMARINE,
  BORDER_GRAY,
  EXOS_GRAY,
  GRAY200,
  ICON_SECONDARY,
  WHITE,
} from '../../theme';
import { formatLongChatDate, time } from '../../utils/date';
import { IMAGE_REGEX } from '../../utils/regex';
import { EventCategories, EventNames } from '../../utils/trackingEvents';
import Avatar from '../reusable/Avatar';
import Text, { TextColor, TextKind } from '../reusable/Text';
import Tooltip from '../reusable/Tooltip';
import { useChatContext } from './Context';
import { Image } from './Image';
import { Reactions, TapBack, TapBackFlyout } from './TapBack';
import { Unfurl } from './Unfurl';
import { MessageWithGroupingMetadata } from './util';

enum MessageType {
  sent = 'sent',
  auto = 'auto',
  received = 'received',
}

const DateSeparator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 8px 0;
  position: relative;

  & > p {
    position: relative;
    z-index: 2;
    background: ${WHITE};
    padding: 0 16px;
  }

  &:before {
    content: '';
    display: block;
    height: 1px;
    width: 100%;
    z-index: 1;
    position: absolute;
    background: ${BORDER_GRAY};
  }
`;

const StyledMessageContainer = styled.div<{
  $isSentMessageGroup: boolean;
  $isFirstMessageInGroup: boolean;
}>`
  padding: 0 16px;
  display: flex;
  gap: 8px;
  align-items: end;
  ${({ $isFirstMessageInGroup }) =>
    $isFirstMessageInGroup &&
    css`
      margin-top: 8px;
    `}

  ${({ $isSentMessageGroup }) =>
    $isSentMessageGroup &&
    css`
      justify-content: flex-end;
    `}
`;

const StyledLastMessageMargin = styled.div<{
  $isLastMessageInGroup: boolean;
}>`
  ${({ $isLastMessageInGroup }) =>
    $isLastMessageInGroup &&
    css`
      margin-bottom: 4px;
    `}
`;

const StyledMessageMainContent = styled.div<{ $isSentMessage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  width: 100%;

  &:first-child {
    margin-left: 40px; // avatar width + gap
  }

  ${({ $isSentMessage }) => {
    if ($isSentMessage) {
      return css`
        align-items: flex-end;
      `;
    }
    return '';
  }}
`;

const StyledMessageHeader = styled.div<{ $isSentMessageGroup: boolean }>`
  display: flex;
  gap: 8px;

  ${({ $isSentMessageGroup }) =>
    $isSentMessageGroup &&
    css`
      justify-content: flex-end;
    `}
`;

const StyledMessage = styled.div<{
  $messageType: MessageType;
}>`
  border-radius: 16px;
  padding: 8px 16px;
  max-width: 80%;
  word-break: break-word;
  width: fit-content;
  margin-bottom: 4px;

  ${({ $messageType }) => {
    switch ($messageType) {
      case MessageType.sent:
        return css`
          background: ${AQUAMARINE};
        `;
      case MessageType.auto:
        return css`
          background: ${WHITE};
          border: 1px solid ${GRAY200};
        `;
      case MessageType.received:
      default:
        return css`
          background: ${EXOS_GRAY};
        `;
    }
  }}

  &:hover ${TapBackFlyout} {
    display: flex;
  }

  // Ensure text is still clickable/accessible even with tapback hover
  &:hover > p {
    z-index: 3; // 1 higher than tapback to allow copying text and clicking on links
    position: relative;
  }
`;

/**
 * Tapback positioning is absolute, relative to the closest relative parent
 * This element is meant to be the closest relative parent to the tapback.
 * It covers the height of the message, while being the full width of the chat
 */
const StyledTapbackRelativeParent = styled.div<{
  $isSentMessageGroup: boolean;
}>`
  width: 100%;
  position: relative;
  display: flex;

  justify-content: ${({ $isSentMessageGroup }) =>
    $isSentMessageGroup ? `flex-end` : `flex-start`};
`;

const LinkDecorator = (
  decoratedHref: string,
  decoratedText: string,
  key: number,
  track: (linkUrl: string) => void,
) => (
  <a
    target="_blank"
    rel="noopener noreferrer"
    href={decoratedHref}
    key={key}
    onClick={() => track(decoratedHref)}
    style={{ color: 'inherit' }}
  >
    {decoratedText}
  </a>
);

const getMessageType = (isSentMessageGroup: boolean, isAutomated: boolean) => {
  if (isSentMessageGroup) {
    return MessageType.sent;
  }
  if (isAutomated) {
    return MessageType.auto;
  }
  return MessageType.received;
};

const StyledUnfurlContainer = styled.span`
  &:hover ${TapBackFlyout} {
    display: flex;
  }
`;

export const Message: React.FC<{
  message: MessageWithGroupingMetadata;
  isSentMessage: boolean;
}> = ({ message, isSentMessage }) => {
  const { track } = useTracking();
  const { chatId } = useChatContext();

  const {
    id,
    text,
    isFirstMessageInDay,
    isFirstMessageInGroup,
    isLastMessageInGroup,
    isLastReadMessage,
  } = message;

  const trackLinkClick = (linkUrl: string, isUnfurl?: boolean) =>
    track(EventNames.LinkClick, {
      chat_id: chatId,
      message_id: id,
      is_unfurl: !!isUnfurl,
      event_label: linkUrl,
      event_category: EventCategories.Chat,
    });

  const displayReadReceipt = isLastReadMessage && isSentMessage;
  const isImage = new RegExp(IMAGE_REGEX, 'i').test(text);

  const words = message.text.split(/\s/gm).filter((word) => word.length);

  const isReadableMessage = words.length > message.previews.length;

  return (
    <StyledLastMessageMargin $isLastMessageInGroup={isLastMessageInGroup}>
      {isFirstMessageInDay && (
        <DateSeparator>
          <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
            {formatLongChatDate(message.createdAt)}
          </Text>
        </DateSeparator>
      )}

      <StyledMessageContainer
        data-testid="chat-message-container"
        data-sentby={message.user.id}
        $isSentMessageGroup={isSentMessage}
        $isFirstMessageInGroup={isFirstMessageInGroup}
      >
        {!isSentMessage && isLastMessageInGroup && (
          <Avatar user={message.user} size={32} sx={{ marginBottom: '4px' }} />
        )}
        <StyledMessageMainContent $isSentMessage={isSentMessage}>
          <StyledMessageHeader $isSentMessageGroup={isSentMessage}>
            {!isSentMessage && isFirstMessageInGroup && (
              <Text
                kind={TextKind.BodyS}
                color={TextColor.Secondary}
                testId="chat-message-sender-name"
              >
                {message.user.firstName} {message.user.lastName}
              </Text>
            )}
            {isFirstMessageInGroup && (
              <Text
                kind={TextKind.BodyS}
                color={TextColor.Secondary}
                testId="chat-message-timestamp"
              >
                {time(message.createdAt)}
              </Text>
            )}
          </StyledMessageHeader>
          {isReadableMessage && (
            <StyledTapbackRelativeParent $isSentMessageGroup={isSentMessage}>
              <StyledMessage
                data-testid="chat-message-text-content"
                $messageType={getMessageType(isSentMessage, message.automated)}
                data-itemid={id}
                key={id}
              >
                <TapBack
                  isSentMessage={isSentMessage}
                  message={message}
                  testId="-message"
                />
                {isImage && (
                  <Image
                    data-testid="message-image"
                    fileName="message"
                    src={text}
                    chatId={chatId}
                    messageId={id}
                    isClickable
                  />
                )}

                {!isImage && (
                  <Linkify
                    componentDecorator={(
                      decoratedHref: string,
                      decoratedText: string,
                      key: number,
                    ) =>
                      LinkDecorator(
                        decoratedHref,
                        decoratedText,
                        key,
                        trackLinkClick,
                      )
                    }
                  >
                    <Text
                      kind={TextKind.BodyS}
                      testId="chat-message-text"
                      style={{
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {text}
                    </Text>
                  </Linkify>
                )}
              </StyledMessage>
            </StyledTapbackRelativeParent>
          )}

          <StyledTapbackRelativeParent $isSentMessageGroup={isSentMessage}>
            <StyledUnfurlContainer>
              {!isReadableMessage && (
                <TapBack
                  testId="-unfurl"
                  isSentMessage={isSentMessage}
                  message={message}
                />
              )}
              {message.previews &&
                message.previews.map((preview, i) => (
                  <Unfurl
                    // biome-ignore lint/suspicious/noArrayIndexKey: eslint-migration
                    key={preview.title + i}
                    preview={preview}
                    isSentMessage={isSentMessage}
                    onLinkClick={(linkUrl: string) =>
                      trackLinkClick(linkUrl, true)
                    }
                  />
                ))}
            </StyledUnfurlContainer>
          </StyledTapbackRelativeParent>

          {displayReadReceipt && (
            <Tooltip
              content={
                <Text color={TextColor.Contrast} kind={TextKind.BodyS}>
                  Message read
                </Text>
              }
              placement="top"
            >
              <div style={{ display: 'flex', gap: '4px' }}>
                <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
                  Read
                </Text>
                <FontAwesomeIcon
                  icon={faCheck}
                  color={ICON_SECONDARY}
                  data-testid="read-receipt-tooltip"
                  data-itemid={id}
                />
              </div>
            </Tooltip>
          )}
        </StyledMessageMainContent>
      </StyledMessageContainer>

      <Reactions message={message} isSentMessage={isSentMessage} />
    </StyledLastMessageMargin>
  );
};
